import {Color} from '@benchling/nucleus-ui';
import {Button as MantineButton} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import moment from 'moment-timezone';
import React, {useEffect, useMemo, useState} from 'react';

import * as AuditActions from 'audit/AuditActions';
import {ActionFilterCriteria, AuditLogFormat} from 'audit/AuditDAO';
import BenchlingConfig from 'BenchlingConfig';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import Button from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import ResultsPager from 'ui-library/ResultsPager';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';

import AuditLogColumnVisibilityDropdown from './AuditLogColumnVisibilityDropdown';
import AuditLogActionFilterDropdown from './filters/AuditLogActionFilterDropdown';
import AuditLogDateRangeFilterDropdown from './filters/AuditLogDateRangeFilterDropdown';
import AuditLogReasonCodeFilterDropdown from './filters/AuditLogReasonCodeFilterDropdown';
import AuditLogUserFilterDropdown from './filters/AuditLogUserFilterDropdown';
import useAuditLogData from './useAuditLogData';
import useAuditLogFilters from './useAuditLogFilters';
import useColumnVisibility from './useColumnVisibility';

export interface AuditLogViewModalProps {
  onRequestClose: () => void;
  ids: Array<string>;
  additionalOptions?: {resultsWarehouseIds?: Array<string>; relatedResultsModelId?: string};
}

const PAGE_SIZE = 50;
const EMPTY_ACTION_CRITERIA: Array<ActionFilterCriteria> = [];

export default function AuditLogViewModal(props: AuditLogViewModalProps): JSX.Element {
  const isFilteringEnabled = BenchlingConfig.AUDITING_IN_APP_AUDIT_LOG_FILTERING;

  const {
    dateRange,
    setDateRange,
    dateMode,
    setDateMode,
    selectedUserIds,
    setSelectedUserIds,
    selectedActionValues,
    setSelectedActionValues,
    selectedReasonCodes,
    setSelectedReasonCodes,
    filterUsers,
    filterActions,
    filterReasonCodes,
    filterOptionsError,
    hasActiveFilters,
    clearAllFilters,
    displayTimezone,
    displayDateFormat,
  } = useAuditLogFilters({
    ids: props.ids,
    enabled: isFilteringEnabled,
    resultsWarehouseIds: props.additionalOptions?.resultsWarehouseIds,
    relatedResultsModelId: props.additionalOptions?.relatedResultsModelId,
  });

  const selectedActionCriteria: Array<ActionFilterCriteria> = useMemo(() => {
    if (selectedActionValues.length === 0) {
      return EMPTY_ACTION_CRITERIA;
    }
    const selectedSet = new Set(selectedActionValues);
    return filterActions
      .filter((action) => selectedSet.has(action.displayName))
      .flatMap((action) => action.filters);
  }, [selectedActionValues, filterActions]);

  // Only update the committed date filter when the selection is complete. In "between" mode
  // this means both dates are set (or both cleared). This prevents removing the active filter
  // while the user is mid-selection picking a new range.
  const [committedDateFilter, setCommittedDateFilter] = useState<{
    startDateISO?: string;
    endDateISO?: string;
  }>({});
  useEffect(() => {
    const hasOnlyOneDate = (dateRange.startDate == null) !== (dateRange.endDate == null);
    const isBetweenMidSelection = dateMode === 'between' && hasOnlyOneDate;
    if (isBetweenMidSelection) {
      return;
    }
    setCommittedDateFilter({
      startDateISO: dateRange.startDate
        ? moment.tz(dateRange.startDate, 'YYYY-MM-DD', displayTimezone).startOf('day').format()
        : undefined,
      endDateISO: dateRange.endDate
        ? moment.tz(dateRange.endDate, 'YYYY-MM-DD', displayTimezone).endOf('day').format()
        : undefined,
    });
  }, [dateRange.startDate, dateRange.endDate, dateMode, displayTimezone]);

  const {state, allColumns, loadAuditData} = useAuditLogData({
    ids: props.ids,
    pageSize: PAGE_SIZE,
    resultsWarehouseIds: props.additionalOptions?.resultsWarehouseIds,
    relatedResultsModelId: props.additionalOptions?.relatedResultsModelId,
    userIds: isFilteringEnabled ? selectedUserIds : undefined,
    actions: isFilteringEnabled ? selectedActionCriteria : undefined,
    reasonCodes: isFilteringEnabled ? selectedReasonCodes : undefined,
    startDate: isFilteringEnabled ? committedDateFilter.startDateISO : undefined,
    endDate: isFilteringEnabled ? committedDateFilter.endDateISO : undefined,
  });
  const {isLoading, auditData, currentPage, error} = state;

  const {hiddenColumns, visibleColumns, handleColumnToggle, handleHideAll, handleShowAll} =
    useColumnVisibility(allColumns);

  const filtersDisabled =
    isLoading || filterOptionsError || (!hasActiveFilters && (!auditData || auditData.data.length === 0));

  const renderMainContent = (): JSX.Element => {
    if (isLoading) {
      return (
        <div className={css(styles.loadingContainer)}>
          <HorizontalThrobber />
        </div>
      );
    }

    if (error) {
      return (
        <div className={css(styles.errorContainer)}>
          <div className={css(styles.errorText)}>{error}</div>
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            onClick={() => {
              loadAuditData(currentPage);
            }}
            size={ButtonSize.MEDIUM}
            text="Retry"
          />
        </div>
      );
    }

    if (!auditData || auditData.data.length === 0) {
      return (
        <div className={css(styles.emptyContainer)}>
          <BenchlingIcon icon="audit-trail" className={css(styles.emptyIcon)} />
          <div className={css(styles.emptyText)}>No audit log entries found.</div>
        </div>
      );
    }

    const {data} = auditData;

    return (
      <Table
        numRows={visibleColumns.length ? data.length : 0}
        emptyTableText="All columns hidden. Select columns to display."
        getRowKey={(rowIndex) => rowIndex.toString()}
        isCompact={true}
        showBorder={false}
        overflowVisible={true}
        frozenHeader={true}
      >
        <Column
          key="row-number"
          header=""
          renderCell={(rowIndex: number) => (
            <Cell>
              <div className={css(styles.cellContent, styles.rowNumberCell)}>
                {currentPage * PAGE_SIZE + rowIndex + 1}
              </div>
            </Cell>
          )}
        />
        {visibleColumns.map((column: string) => (
          <Column
            key={column}
            header={column}
            renderCell={(rowIndex: number) => (
              <Cell>
                <div className={css(styles.cellContent)}>{data[rowIndex]?.[column] || 'N/A'}</div>
              </Cell>
            )}
          />
        ))}
      </Table>
    );
  };

  const renderStickyHeader = (): JSX.Element => (
    <div className={css(styles.stickyHeaderContainer)}>
      <div className={css(styles.toolbarContainer)}>
        {isFilteringEnabled ? (
          <div className={css(styles.rowGroup)}>
            <AuditLogDateRangeFilterDropdown
              value={dateRange}
              onChange={setDateRange}
              mode={dateMode}
              onModeChange={setDateMode}
              disabled={filtersDisabled}
              displayDateFormat={displayDateFormat}
            />
            <AuditLogUserFilterDropdown
              users={filterUsers}
              selectedUserIds={selectedUserIds}
              onChange={setSelectedUserIds}
              disabled={filtersDisabled}
            />
            <AuditLogActionFilterDropdown
              actions={filterActions}
              selectedActionValues={selectedActionValues}
              onChange={setSelectedActionValues}
              disabled={filtersDisabled}
            />
            {filterReasonCodes != null && (
              <AuditLogReasonCodeFilterDropdown
                reasonCodes={filterReasonCodes}
                selectedReasonCodes={selectedReasonCodes}
                onChange={setSelectedReasonCodes}
                disabled={filtersDisabled}
              />
            )}
            {hasActiveFilters && (
              <MantineButton
                variant="ghost"
                size="sm"
                leftIcon={<BenchlingIcon icon="xmark-large" />}
                onClick={clearAllFilters}
              >
                Clear all
              </MantineButton>
            )}
          </div>
        ) : (
          <AuditLogColumnVisibilityDropdown
            allColumns={allColumns}
            hiddenColumns={hiddenColumns}
            onColumnToggle={handleColumnToggle}
            onHideAll={handleHideAll}
            onShowAll={handleShowAll}
            disabled={filtersDisabled}
          />
        )}
        <div className={css(styles.rowGroup)}>
          {isFilteringEnabled && (
            <AuditLogColumnVisibilityDropdown
              allColumns={allColumns}
              hiddenColumns={hiddenColumns}
              onColumnToggle={handleColumnToggle}
              onHideAll={handleHideAll}
              onShowAll={handleShowAll}
              disabled={filtersDisabled}
            />
          )}
          <Dropdown
            menu={
              <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM} dropLeft={true}>
                <Option
                  dataTestAttrs={{component: 'AuditLogViewModal', element: 'exportCsvOption'}}
                  onSelect={() => {
                    AuditActions.initiateAuditLogExport(
                      props.ids,
                      AuditLogFormat.CSV,
                      props.additionalOptions || {}
                    );
                  }}
                >
                  Export CSV
                </Option>
                <Option
                  dataTestAttrs={{component: 'AuditLogViewModal', element: 'exportPdfOption'}}
                  onSelect={() => {
                    AuditActions.initiateAuditLogExport(
                      props.ids,
                      AuditLogFormat.PDF,
                      props.additionalOptions || {}
                    );
                  }}
                >
                  Export PDF
                </Option>
              </Menu>
            }
            closeOnInsideClick={true}
          >
            <Button
              dataTestAttrs={{component: 'AuditLogViewModal', element: 'exportButton'}}
              buttonStyle={ButtonStyle.TERTIARY_BLUE}
              icon="download"
              size={ButtonSize.SMALL}
              text="Export"
              isDropdown={true}
            />
          </Dropdown>
        </div>
      </div>
      {/* Normally we would render the ResultsPager using the props provided by SimpleTable. But we want the
      pager to be sticky at the top of the modal along with the table's sticky header */}
      {auditData?.pagination && (
        <ResultsPager
          allowFastForwardAndBackward={true}
          isLoading={isLoading}
          pagination={{
            offset: currentPage * PAGE_SIZE,
            limit: PAGE_SIZE,
            total: auditData.pagination.total,
          }}
          setOffset={(newOffset: number) => {
            const newPage = Math.floor(newOffset / PAGE_SIZE);
            loadAuditData(newPage);
          }}
        />
      )}
    </div>
  );

  return (
    <Modal
      enableRedesign={true}
      title={auditData?.objectMetadata?.title || 'Audit Log'}
      onRequestClose={props.onRequestClose}
      extraLarge={true}
      scrollable={false}
      expandHeight={false}
      buttons={[
        {
          text: 'Close',
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: props.onRequestClose,
        },
      ]}
      dataTestAttrs={{component: 'AuditLogViewModal', element: 'root'}}
    >
      <div className={css(styles.modalContainer)}>
        {renderStickyHeader()}
        <div className={css(styles.scrollableContainer)}>{renderMainContent()}</div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 220px)',
  },
  stickyHeaderContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  toolbarContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  rowGroup: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    alignItems: 'center',
  },
  scrollableContainer: {
    flex: 1,
    overflow: 'auto',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
    textAlign: 'center',
  },
  errorText: {
    marginBottom: '16px',
    color: Color.GRAY_400,
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '48px',
    color: Color.GRAY_50,
    marginBottom: '16px',
  },
  emptyText: {
    color: Color.GRAY_400,
  },
  cellContent: {
    paddingTop: '10px',
    paddingBottom: '10px',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    lineHeight: '1.5',
    fontSize: '14px',
    minHeight: '20px',
    maxWidth: '400px',
    width: 'max-content',
  },
  rowNumberCell: {
    textAlign: 'center',
    color: Color.GRAY_400,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    width: '30px',
  },
});
